import { HttpClient } from '@angular/common/http';
import { AfterContentChecked, Component, HostListener, inject, Input, signal, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
    faAngleDown,
    faArrowLeft,
    faArrowRight,
    faBuilding,
    faBuildingColumns,
    faClipboardList,
    faHouse,
    faQuestionCircle,
    faSwap,
    faUser,
    faUserCog,
    faUsers,
    faVirus
} from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { PermissionProfile } from '../../../core/enums/generated/PermissionProfile';
import { getMainNavRoutes } from '../../../core/main-nav-routes';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessageService } from '../../../core/system-message/system-message-service';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { EventMouVm } from '../../generated/Administration/Models/User/JavaScript/EventMouVm';
import { UserJsVm } from '../../generated/Administration/Models/User/JavaScript/UserJsVm';
import { SwapEventVm } from '../../generated/Administration/Models/User/SwapEventVm';
import { pcgSettings } from '../../generated/pcg-settings';
import { SelectItem } from '../../models/select-item';
import { GlobalService } from '../../service/global.service';
import { MouAckComponent } from '../mou-ack/mou-ack.component';
import { NavigationService } from '../navigation.service';

@UntilDestroy()
@Component({
    selector: 'pcg-mat-side-nav',
    templateUrl: './mat-side-nav.component.html',
    styleUrls: ['./mat-side-nav.component.scss'],
    standalone: false
})
export class MatSideNavComponent implements AfterContentChecked {

	@ViewChild('modalChangeEvent', { static: true }) modalChangeEvent!: TemplateRef<any>;

	@Input() user!: UserJsVm;

    navService = inject(NavigationService);

    navContentOffscreen: boolean = false;
	// Setting the scroll height used to keep the nested table scrollable with headers shown.
	observer = new IntersectionObserver(entries => { this.navContentOffscreen = !entries[0]?.isIntersecting; });
	// Used to display a loading spinner on the navigation link when clicked, notifying the user that the page is loading.
	navLinkLoading = signal<[string, number]>([null!, null!]);

    eventItems: SelectItem[] = [];
	formGroup = SwapEventVm.Form;
	model = SwapEventVm.GetModel(this.formGroup);	
	
    mode = pcgSettings.mode;
	eventName: string = "";
    hoverText: string = "";
    navName = "";

	organizationId = 0;
    currentEventId = 0;
    navigationOffset = 0;

  	navOpened!: boolean;  	
    isAdmin = false;
	canManageUsers = false;
	isOrganizationAdmin = false;
	isSiteUser = false;
	isSiteManager = false;
	isAckOpen = false;

	// Fa icons
	faIconName = { 
		faAngleDown
		, faUser
		, faUserCog
		, faQuestionCircle
		, faBuildingColumns
		, faBuilding
		, faClipboardList
		, faUsers
		, faVirus
		, faArrowRight
		, faArrowLeft
        , faSwap
        , faHouse
	};

	xDown = null;
	yDown = null;

	constructor(
		public router: Router
		, private sec: SecurityService
		, private modalService: NgbModal
		, private httpClient: HttpClient
		, public globalService: GlobalService
		, private gvs: GlobalVariablesService
        , private ms: SystemMessageService
	) { }

    @HostListener('window:resize')
	onResize() { this.fixNavOffset(); }

  	ngOnInit() { 
        this.navService.navRoutes.set(this.sec.getSecureNavItems(getMainNavRoutes()));
		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        this.navService.navRoutes$
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.fixNavOffset();
				this.navLinkLoading.set([null!, null!]);
			}
		);

		this.gvs.currentEventName.set(this.user?.currentEventName!);
        this.eventName = this.gvs.currentEventName();

        if (
            this.eventName?.length > 20
            && this.eventName?.includes(" ")
        ) { this.eventName = this.eventName?.split(" ")[0]; }

		this.gvs.currentEventId.set(this.user?.currentEventId);	
        this.currentEventId = this.gvs.currentEventId();

		var eventMous: EventMouVm[] | undefined = this.user?.eventMous?.filter(o => o.eventId == this.currentEventId);
		if (
            eventMous != null 
            && eventMous[0]?.hasAcknowledgedMOU != true 
            && this.isAckOpen == false
        ) { this.openAck(); }

		this.getEvents();
		
		//event litseners for swiping away side nav in mobile
		document.addEventListener('touchstart', (evt) => this.handleTouchStart(evt), false);        
		document.addEventListener('touchmove', (evt) => this.handleTouchMove(evt), false);

        // Checking if there is any nave content off-screen.
		const navAnchor = document.getElementById("navAnchor");
		this.observer?.observe(navAnchor!);
	}

    fixNavOffset() {
		const mainNav = document.getElementById("pcgMainNavigation");
        if (mainNav) {
            setTimeout(() => {
                if (getComputedStyle(mainNav!, null).position === "fixed") { this.navigationOffset = mainNav?.getBoundingClientRect()?.bottom!; } 
                else { this.navigationOffset = 0; }

                const styles = `pcg-side-nav { top: ${this.navigationOffset - 1}px }` 
                    + `.nav-padding-placeholder { padding-top: ${this.navigationOffset}px; }` 
                    + `.conversations-sidenav { top: ${this.navigationOffset}px }` 
                    + `.knowledge-base-doc-popout { top: ${this.navigationOffset}px }` 
                    + `.popout { top: ${this.navigationOffset}px }`;

                const tableStyles = `.pcg-table-fixed-header thead tr th, .pcg-table-fixed-header thead tr { top: ${this.navigationOffset - 1}px }`;

                this.setDynamicStyle("navPaddingOffset", styles);
                this.setDynamicStyle("navTableOffset", tableStyles);
            }, 100);
        }
	}

    setDynamicStyle(
        name: string
        , styles: string
    ) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		dynamicStyle = document.createElement("style");
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

    // Navigate/redirect to new page
	redirect(path: string) { this.router.navigateByUrl(path); }

	ngAfterContentChecked() {
		this.isAdmin = this.sec.hasAccess(o => o.adminFeatures);
		this.canManageUsers = this.sec.hasAccess(o => o.manageSiteUsers);
		this.isOrganizationAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.OrganizationAdmin);
		this.isSiteUser = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.SiteUser);
		this.isSiteManager = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.SiteManager);
		this.organizationId = this.user?.organizationId;
	}

    openInNewTab(namedRoute: string) {
		let newRelativeUrl = this.router.createUrlTree([namedRoute]);
		let baseUrl = window.location.href.replace(this.router.url, '');
		window.open(baseUrl + newRelativeUrl, '_blank');
		this.navService.isNavOpen.set(false);
	}

    toggleUserProfileSubNav() {
		const isOpen = this.navService.userProfileExpanded();
		this.navService.collapseAllSubNavs();
		if (!isOpen) { this.navService.userProfileExpanded.set(true); }
	}

    handleTouchStart(evt: any) {
		if (
			window.innerWidth < 993 
			&& this.navService.isNavOpen()
		) {
			let firstTouch = evt.touches || evt.originalEvent.touches;       
			this.xDown = firstTouch[0].clientX;                                      
			this.yDown = firstTouch[0].clientY; 
		}			                                   
	};                                                
																				
	handleTouchMove(evt: any) {
		if (
			this.navService.isMobile() 
			&& this.navService.isNavOpen()
		) {
			if (
				!this.xDown 
				|| !this.yDown
			) { return; }
	
			let xUp = evt.touches[0].clientX;                                    
			let yUp = evt.touches[0].clientY;
			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;
																				
			if (Math.abs(xDiff) > Math.abs(yDiff)) {/*most significant*/
				if (xDiff > 8) {
					/* right swipe */ 
					this.navService.isNavOpen.set(false);
				} else { /* left swipe */ }                       
			} else {
				if (yDiff > 8) { /* down swipe */ } 
				else { /* up swipe */ }                                                                 
			}
			/* reset values */
			this.xDown = null;
			this.yDown = null;  
		}
	}

    navLinkClicked(
		name: any
		, level: any
        , openNewTab = false
        , path = ""
	) {
        if (openNewTab == true) { window.open(path); }
        else {
            if (this.navService.isMobile()) { this.navService.isNavOpen.set(false); }
            this.navLinkLoading.set([name, level]);
            setTimeout(() => { this.navLinkLoading.set([null!, null!]); }, 500);
        }        
	}

	navLinkIsLoading(
		name: any
		, level: any
	) {
		const [currentName, currentLevel] = this.navLinkLoading();
		return (currentName === name && currentLevel === level);
	}

	scrollToBottom() {
		const navAnchor = document.getElementById('navAnchor');
		if (navAnchor) { navAnchor.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }); }
	}

    // #region Logout
    logout() { this.executeLogout(); }

    executeLogout() {
        this.gvs.isNavFixed.set(false);
        this.navService.collapseAllSubNavs();
        this.sec.setSecurity(null!, null!, null!);
        localStorage.removeItem("subNavPosition");
        sessionStorage.clear();
        this.deleteAllCookies();
        
        var loggedInUsingSso = localStorage.getItem("loggedInUsingSso") == "true";
        localStorage.setItem("loggedInUsingSso", "false");
        if (loggedInUsingSso) {
            this.httpClient.get(
                `api/Account/SingleSignOut` 
                + `?userId=${this.user.id}` 
                + `&entraIdentifierUrl=${localStorage.getItem("entraIdentifierUrl")}` 
                + `&entraLoginUrl=${localStorage.getItem("entraLoginUrl")}`
            ).subscribe((msg: any) => {
                localStorage.setItem("entraIdentifierUrl", "");
                localStorage.setItem("entraLoginUrl", "");
                if (msg.isSuccessful) { window.location.href = msg.value; }
                else { this.ms.setSystemMessage(msg.message, msg.messageClass); }
            });
        }
        this.navService.setOpenSecondaryMenu([]);
        this.router.navigate(['/login']);
    }

    deleteAllCookies() {
        const cookies = document?.cookie?.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
    }
    //#endregion  

    // #region Change Event
	getEvents() { this.httpClient.get(`api/Select/Events`).subscribe((items: any) => { this.eventItems = items; }); }

	openChangeEventModal() { 
		this.modalService.open(this.modalChangeEvent, { beforeDismiss: () => { return true; } });
		this.model = SwapEventVm.GetModel(this.formGroup);
		this.formGroup.controls.eventId.patchValue(this.user.currentEventId);
	}

	changeEvent(val: number) {
		this.currentEventId = val;
		this.model.eventId = this.currentEventId;
		this.formGroup.controls.eventId.patchValue(this.currentEventId);
	}

	saveChangeEvent(modal: { dismiss: () => void; }) {
		this.httpClient.post('api/SetEvent', this.formGroup.getRawValue())
            .subscribe(async (sm: any) => {
                if (sm.isSuccessful) { 		
					var newUserInfo: UserJsVm = sm.value;	
					this.sec.setSecurity(undefined!, newUserInfo, undefined!);
					this.eventName = newUserInfo.currentEventName!;
					this.gvs.currentEventName.set(newUserInfo.currentEventName!);
                    if (
                        this.eventName.length > 20
                        && this.eventName.includes(" ")
                    ) { this.eventName = this.eventName.split(" ")[0]; }
					this.currentEventId = newUserInfo.currentEventId;
					this.gvs.currentEventId.set(newUserInfo.currentEventId);
					
					if (newUserInfo?.eventMous?.length! > 0) {
						var eventMous: any = newUserInfo?.eventMous?.filter(o => o.eventId == newUserInfo?.currentEventId);
						if (eventMous[0]?.hasAcknowledgedMOU !== true) { this.openAck(); }
					}

					if (this.router.url === '/dashboard') { window.location.reload(); }
					else { this.router.navigateByUrl(`dashboard`); }
					modal.dismiss();	
				} 
            }
        );
	}
    // #endregion
	
	openAck() {
		this.isAckOpen = true;
		this.modalService.open(MouAckComponent, { backdrop: 'static', scrollable: true });
	}
}